import { FC, useMemo, useState } from 'react';
import type { DreMes, DrePayload } from '@/types/dashboards';
import { composicaoDoMes, desviosDoMes, kpisDoMes, mesDre, seriePctCustos, serieMetrica } from '@/utils/equiplexDre';
import { mesVigente } from '@/utils/filtroPaineis';
import { periodoMesCivil } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS, type CoresPainel } from '../chartTheme';
import RotuloMes from '../RotuloMes';
import AvisosDre from './AvisosDre';
import ComposicaoCustoTable from './ComposicaoCustoTable';
import ContasSemClassificacao from './ContasSemClassificacao';
import CustosPctChart from './CustosPctChart';
import DesviosOrcadoTable from './DesviosOrcadoTable';
import DreTable, { type ModoDre } from './DreTable';
import KpisDre from './KpisDre';
import ResultadoChart from './ResultadoChart';

interface Props {
  payload: DrePayload;
  cores: CoresPainel;
}

const SeletorMes: FC<{ meses: DreMes[]; mes: string; onMes: (m: string) => void }> = ({ meses, mes, onMes }) => (
  <div role="group" aria-label="Mês" className="flex flex-wrap items-center gap-1.5">
    <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Mês</span>
    {meses.map((m) => {
      const ativo = m.key === mes;
      return (
        <button
          key={m.key}
          type="button"
          aria-pressed={ativo}
          title={`${m.label}: mês civil, ${periodoMesCivil(m.key)}`}
          onClick={() => onMes(m.key)}
          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
            ativo
              ? 'border-accent bg-accent font-semibold text-accent-contrast'
              : 'border-theme-border text-content-secondary hover:text-content-primary'
          }`}
        >
          <RotuloMes label={m.label} periodo={periodoMesCivil(m.key)} />
          {m.contabil_incompleto && <span className="font-normal">· parcial</span>}
        </button>
      );
    })}
  </div>
);

const Notas: FC<{ notas: string[] }> = ({ notas }) => (
  <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="dre-notas">
    <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
    <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
      {notas.map((n) => (
        <li key={n}>{n}</li>
      ))}
      <li>Fonte: réplica MySQL do Datasul (contábil e orçamento copiados pelo Azure Data Factory); painel gerado às 06:00.</li>
    </ul>
  </details>
);

/**
 * DRE e desvios P&L: a DRE do Manus com o contábil real. Em cima, o mês escolhido
 * (números, avisos, orçado × realizado); no meio, a DRE inteira mês a mês e os gráficos;
 * embaixo, a composição do custo e as contas que o Datasul não classifica. Os meses são os do
 * período do filtro dos painéis; os chips escolhem o mês dentro dele.
 */
const PainelDre: FC<Props> = ({ payload, cores }) => {
  const [mesEscolhido, setMes] = useState<string | null>(null);
  const mes = mesVigente(mesEscolhido, payload.meses, payload.mes_padrao ?? '');
  const [modo, setModo] = useState<ModoDre>('valor');
  const [metrica, setMetrica] = useState('lucro_bruto');

  const mesDesc = mesDre(payload, mes);
  const kpis = useMemo(() => kpisDoMes(payload, mes), [payload, mes]);
  const serie = useMemo(() => serieMetrica(payload, metrica), [payload, metrica]);
  const custos = useMemo(() => seriePctCustos(payload), [payload]);
  const desvios = useMemo(() => desviosDoMes(payload, mes), [payload, mes]);
  const composicao = useMemo(() => composicaoDoMes(payload, mes), [payload, mes]);

  if (!payload.meses.length || !mesDesc) {
    return (
      <Aviso icone="event_busy" cor={STATUS.atencao} titulo="Sem lançamento contábil" testId="dre-vazio">
        A réplica não tem lançamento contábil da Equiplex neste período (a DRE começa em janeiro de 2026).
      </Aviso>
    );
  }

  return (
    <>
      {payload.meses.length > 1 && <SeletorMes meses={payload.meses} mes={mes} onMes={setMes} />}
      <AvisosDre payload={payload} mes={mesDesc} />
      <KpisDre kpis={kpis} parcial={mesDesc.contabil_incompleto} />
      <DreTable linhas={payload.linhas} meses={payload.meses} mes={mes} modo={modo} onModo={setModo} />
      {/* Um gráfico por linha (26/10/2026). */}
      <ResultadoChart serie={serie} metrica={metrica} onMetrica={setMetrica} mesSelecionado={mes} cores={cores} />
      <CustosPctChart serie={custos} cores={cores} />
      <DesviosOrcadoTable
        linhas={desvios}
        mesLabel={mesDesc.label}
        regras={payload.regras}
        temOrcado={mesDesc.tem_orcado}
        parcial={mesDesc.contabil_incompleto}
      />
      <ComposicaoCustoTable linhas={composicao.linhas} cpv={composicao.cpv} mesLabel={mesDesc.label} />
      <ContasSemClassificacao contas={payload.contas_sem_classificacao} />
      <Notas notas={payload.notas} />
    </>
  );
};

export default PainelDre;
